/** @jsxImportSource @emotion/react */
import {
  FC,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useState,
  ChangeEvent,
} from 'react';
import { VerifySettlementIllustration } from 'public/illustrations';
import {
  DirectDebitDialog,
  GuidedFileUploader,
  GuidedFileUploaderRef,
  NavButtons,
} from 'components';
import { useMerchantApplicationContext, useSnackbar } from 'contexts';
import { StepComponentProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import {
  Buttons,
  TextFields,
  ThemeProvider,
  Widgets,
} from '@zip/business-components';
import { useNavigate } from 'react-router-dom';
import { css, theme } from 'global';

import * as styles from './SettlementDetailsStep.styles';
import {
  BankStatementSuggestionsResponse,
  logEvent,
  removeWhitespace,
  useActivationFunctions,
  Validators,
} from 'utils';
import { isAbortError } from 'types/common/ApiHelpers';
import { PageAction, PageRoute } from 'enums';
import {
  ActivationProgressStatus,
  IdentityDocumentType,
  SettlementDetails,
} from 'types/responses/activation';
import { renderFieldError } from 'utils/validators';

type SettlementDetailUploadStage =
  | 'upload'
  | 'confirm-loading'
  | 'confirm-form';

type SettlementDetailConfirmFormValues = {
  bank: string;
  bankStatementStorage: string;
  bsb: string;
  accountNumber: string;
  disbursementEmails: string[];
  accountName: string;
  directDebitAgreementSigned: boolean;
  directDebitAgreementSignedTimeStamp: Date;
};

const getEmptyConfirmFormValues = (): SettlementDetailConfirmFormValues => ({
  bank: '',
  bankStatementStorage: '',
  bsb: '',
  accountNumber: '',
  disbursementEmails: [''],
  accountName: '',
  directDebitAgreementSigned: false,
  directDebitAgreementSignedTimeStamp: new Date(),
});

const getBankStatementConfirmFormValuesFromDB = (
  settlementDetails: SettlementDetails
): SettlementDetailConfirmFormValues => {
  const emailValue = settlementDetails.disbursementEmail;
  let disbursementEmails: string[] = [''];

  if (emailValue) {
    const emails = emailValue
      .split(',')
      .map((e: string) => e.trim())
      .filter(Boolean);
    disbursementEmails = emails.length > 0 ? emails : [''];
  }

  return {
    bank: settlementDetails.bank ?? '',
    bankStatementStorage: settlementDetails.bankStatementStorage ?? '', // s3 file path
    bsb: settlementDetails.bsb ?? '',
    accountNumber: settlementDetails.accountNumber ?? '',
    disbursementEmails,
    accountName: settlementDetails.accountName ?? '',
    directDebitAgreementSigned:
      settlementDetails.directDebitAgreementSigned ?? false,
    directDebitAgreementSignedTimeStamp: null, // new date assigned before patch
  };
};

const getBankStatementConfirmFormValuesFromSuggestion = (
  suggestionsResponse: BankStatementSuggestionsResponse
): SettlementDetailConfirmFormValues => {
  return {
    bank: suggestionsResponse.bank_name ?? '',
    bankStatementStorage: suggestionsResponse.file_path ?? '', // s3 file path from AI endpoint but we don't really use it since we need to strip out s3 bucket url
    bsb: removeWhitespace(suggestionsResponse.bsb ?? ''),
    accountNumber: removeWhitespace(suggestionsResponse.account_number ?? ''),
    accountName: suggestionsResponse.name ?? '',
    disbursementEmails: [''],
    directDebitAgreementSigned: false,
    directDebitAgreementSignedTimeStamp: null,
  };
};

// ------------------- Start Can be refactor ---------------------------------------

type ConfirmFormErrors = Partial<
  Record<keyof SettlementDetailConfirmFormValues, string>
> & {
  disbursementEmailErrors?: Record<number, string>;
};

const getConfirmFormErrors = (
  values: SettlementDetailConfirmFormValues
): ConfirmFormErrors => {
  const errors: ConfirmFormErrors = {};

  // BSB validation
  if (!values.bsb) {
    errors.bsb = 'Please enter BSB';
  } else if (values.bsb && !/^\d{6}$/.test(values.bsb)) {
    errors.bsb = 'BSB must be 6 digits';
  }

  // Account Number validation
  if (!values.accountNumber) {
    errors.accountNumber = 'Please enter account number';
  } else if (values.accountNumber && !/^\d{6,10}$/.test(values.accountNumber)) {
    errors.accountNumber = 'Account number must be between 6 and 10 digits';
  }

  // Account Name validation
  if (!values.accountName) {
    errors.accountName = 'Please enter account name';
  } else if (values.accountName.length > 100) {
    errors.accountName = 'Account name cannot exceed 100 characters';
  }

  // Validate at least one email exists and is not empty
  const hasValidEmail = values.disbursementEmails.some(
    (email) => email.trim() !== ''
  );
  if (!hasValidEmail) {
    errors.disbursementEmails = 'Please enter email address';
  } else {
    // Validate email format and length for each email
    const emailErrors: Record<number, string> = {};

    values.disbursementEmails.forEach((email, index) => {
      if (email.trim() !== '') {
        if (email.length > 100 || !Validators.validateEmail(email)) {
          emailErrors[index] = 'Must be a valid email address';
        }
      }
    });

    if (Object.keys(emailErrors).length > 0) {
      errors.disbursementEmailErrors = emailErrors;
    }
  }

  if (!values.directDebitAgreementSigned) {
    errors.directDebitAgreementSigned =
      'You must accept the Direct Debit Request and agree to terms.';
  }

  return errors;
};

const buildSettlementDetailsPatchPayload = (
  values: SettlementDetailConfirmFormValues
) => ({
  settlementDetails: {
    bank: values.bank,
    bankStatementStorage: values.bankStatementStorage,
    bsb: values.bsb,
    accountNumber: values.accountNumber,
    // Concatenate all non-empty emails with comma separator
    disbursementEmail: values.disbursementEmails
      .map((email) => email.trim())
      .filter(Boolean)
      .join(','),
    accountName: values.accountName,
    directDebitAgreementSigned: values.directDebitAgreementSigned,
    directDebitAgreementSignedTimeStamp: new Date(),
  },
});

type UploadContent = {
  title: string;
  requirements: string[];
  infoMessage?: string;
  multiple: boolean;
  documentCategory: IdentityDocumentType;
};

const MIN_VERIFICATION_DURATION_MS = 5000;
const SUGGESTIONS_TIMEOUT_MS = 5000;
const CONFIRM_DETAILS_LOADING_MS = 500;
const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
const MAX_EMAIL_ADDRESSES = 10;
const ACCEPTED_FILE_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
]);

const isAcceptedFileType = (file: File): boolean => {
  if (ACCEPTED_FILE_TYPES.has(file.type)) {
    return true;
  }

  const lowerCaseFileName = file.name.toLowerCase();
  return (
    lowerCaseFileName.endsWith('.pdf') ||
    lowerCaseFileName.endsWith('.jpeg') ||
    lowerCaseFileName.endsWith('.jpg') ||
    lowerCaseFileName.endsWith('.png')
  );
};

const isValidUploadFile = (file: File): boolean => {
  return isAcceptedFileType(file) && file.size <= MAX_FILE_SIZE_BYTES;
};

const areAllFilesValid = (files: File[]): boolean =>
  files.every(isValidUploadFile);

const uploadFileToS3 = async (
  url: string,
  file: File,
  signal: AbortSignal
): Promise<void> => {
  const res = await fetch(url, {
    method: 'PUT',
    body: file,
    signal,
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to upload ${file.name} to S3`);
  }
};

const waitForDelay = (durationMs: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, durationMs);

    const handleAbort = (): void => {
      window.clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    signal.addEventListener('abort', handleAbort, { once: true });
  });

const waitForMinimumDuration = async (
  startedAtMs: number,
  minimumDurationMs: number,
  signal: AbortSignal
): Promise<void> => {
  const elapsedMs = Date.now() - startedAtMs;
  const remainingMs = minimumDurationMs - elapsedMs;

  if (remainingMs > 0) {
    await waitForDelay(remainingMs, signal);
  }
};

const withTimeout = async <T,>(
  promise: Promise<T>,
  durationMs: number,
  signal: AbortSignal,
  timeoutMessage: string
): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      reject(new Error(timeoutMessage));
    }, durationMs);

    const handleAbort = (): void => {
      window.clearTimeout(timeoutId);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    promise.then(
      (value) => {
        window.clearTimeout(timeoutId);
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });

// MC-4775 Will not validate for now - To be re-introduced once confidence score is added in AI endpoint
const getBankStatementVerificationResults = (
  files: File[],
  suggestionsResponse?: BankStatementSuggestionsResponse | null
): boolean[] => {
  return [
    // suggestionsResponse?.file_path !== undefined
    //   ? Boolean(suggestionsResponse.file_path)
    //   : true,
    // suggestionsResponse?.less_than_3_months_old !== undefined
    //   ? Boolean(suggestionsResponse.less_than_3_months_old)
    //   : true,
    // suggestionsResponse?.bsb !== undefined &&
    // suggestionsResponse?.account_number !== undefined &&
    // suggestionsResponse?.name !== undefined
    //   ? Boolean(
    //       suggestionsResponse.bsb &&
    //         suggestionsResponse.account_number &&
    //         suggestionsResponse.name
    //     )
    //   : true,
    true,
    true,
    true,
    areAllFilesValid(files),
  ];
};

// ------------------- End Can be refactor ---------------------------------------

const BANKSTATEMENT_UPLOAD_CONTENT: UploadContent = {
  title: 'Upload a bank statement',
  requirements: [
    'Upload a copy of an original bank statement',
    'Issued in the last 3 months',
    'Includes BSB, account number, and account holder name',
    'PDF, JPEG, or PNG accepted - Max 8 MB each',
  ],
  multiple: false,
  documentCategory: IdentityDocumentType.BankStatement,
};

type SettlementDetailsStepModuleProps = StepComponentProps & {
  stepProgress?: ActivationProgressStatus;
  onBackToDirectorDetails?: () => void;
};

export const SettlementDetailsStepModule: FC<
  SettlementDetailsStepModuleProps
> = ({ stepProgress, onBackToDirectorDetails }) => {
  const navigate = useNavigate();
  const {
    abortRequests,
    getActivationData,
    getMerchantApplicationUploadUrlAsync,
    getBankStatementSuggestionsAsync,
    patchMerchantApplicationAsync,
    patchApplicationProgressAsync,
    submitMerchantApplicationAsync,
  } = useActivationFunctions();
  const { activationData, merchantApplicationId, refreshActivationData } =
    useMerchantApplicationContext();
  const Snackbar = useSnackbar();
  const uploaderRef = useRef<GuidedFileUploaderRef>(null);
  const hasAppliedCachePrefillRef = useRef(false);
  const shouldStartAtConfirmFormRef = useRef(
    stepProgress === ActivationProgressStatus.Edit ||
      stepProgress === ActivationProgressStatus.Confirmed
  );

  const verificationCacheRef = useRef<{
    files: File[];
    results: boolean[];
    formValues: SettlementDetailConfirmFormValues;
  } | null>(null);

  const [stage, setStage] = useState<SettlementDetailUploadStage>('upload');
  const verificationRunIdRef = useRef(0);
  const verificationAbortControllerRef = useRef<AbortController | null>(null);
  const [isDirectDebitDialogOpen, setIsDirectDebitDialogOpen] =
    useState<boolean>(false);

  const [loadedFiles, setLoadedFiles] = useState<File[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const skipConfirmLoadingRef = useRef(false);
  const [isSubmittingConfirmForm, setIsSubmittingConfirmForm] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [confirmFormValues, setConfirmFormValues] =
    useState<SettlementDetailConfirmFormValues>(getEmptyConfirmFormValues);

  const continueLabel = stage === 'upload' ? 'Verify' : 'Submit';
  const canContinue =
    stage === 'upload'
      ? loadedFiles.length > 0 && !isVerifying
      : !isSubmittingConfirmForm;

  const progressPercent = useMemo(() => {
    if (stage === 'confirm-loading' || stage === 'confirm-form') {
      return 90;
    }

    return 75;
  }, [stage]);

  const handleFilesChange = useCallback((files: File[]): void => {
    setLoadedFiles((currentFiles) => {
      const hasSameFiles =
        currentFiles.length === files.length &&
        currentFiles.every((file, index) => file === files[index]);

      return hasSameFiles ? currentFiles : files;
    });
  }, []);

  const abortVerification = useCallback((): void => {
    verificationRunIdRef.current += 1;
    setIsVerifying(false);
    abortRequests();
    verificationAbortControllerRef.current?.abort();
    verificationAbortControllerRef.current = null;
  }, [abortRequests]);

  const handleCancelVerification = useCallback((): void => {
    abortVerification();
  }, [abortVerification]);

  const handleUploaderVerify = useCallback(
    async (files: File[]): Promise<boolean[]> => {
      logEvent(PageAction.merchantOnboardingUAUploadSettlementDocument, {
        merchantApplicationId,
      });

      const selectedUploadContent = BANKSTATEMENT_UPLOAD_CONTENT;
      skipConfirmLoadingRef.current = false;

      const cache = verificationCacheRef.current;
      if (
        cache !== null &&
        files.length === cache.files.length &&
        files.every((f, i) => f === cache.files[i])
      ) {
        setConfirmFormValues(cache.formValues);
        return cache.results;
      }

      if (!areAllFilesValid(files)) {
        return getBankStatementVerificationResults(files, null);
      }

      try {
        verificationAbortControllerRef.current = new AbortController();
        const { signal } = verificationAbortControllerRef.current;
        const verificationStartedAtMs = Date.now();

        const uploadedFilePaths: string[] = [];

        for (const file of files) {
          const uploadUrlResponse = await getMerchantApplicationUploadUrlAsync(
            selectedUploadContent.documentCategory,
            file.name
          );
          await uploadFileToS3(uploadUrlResponse.url, file, signal);

          uploadedFilePaths.push(uploadUrlResponse.s3ObjectUri);
        }

        const bankStatementStorage = uploadedFilePaths[0].replace(
          /^s3:\/\/[^/]+\//,
          ''
        );

        let suggestionsResponse: BankStatementSuggestionsResponse;
        try {
          suggestionsResponse = await withTimeout(
            getBankStatementSuggestionsAsync(uploadedFilePaths[0]),
            SUGGESTIONS_TIMEOUT_MS,
            signal,
            'BankStatement suggestions timed out'
          );
        } catch (error) {
          if (isAbortError(error)) {
            throw error;
          }

          await waitForMinimumDuration(
            verificationStartedAtMs,
            MIN_VERIFICATION_DURATION_MS,
            signal
          );
          skipConfirmLoadingRef.current = true;
          setConfirmFormValues({
            ...getEmptyConfirmFormValues(),
            bankStatementStorage: bankStatementStorage,
          });
          return selectedUploadContent.requirements.map(() => true);
        }

        await waitForMinimumDuration(
          verificationStartedAtMs,
          MIN_VERIFICATION_DURATION_MS,
          signal
        );
        const bankStatementFormValue =
          getBankStatementConfirmFormValuesFromSuggestion(suggestionsResponse);
        const bankStatementResults = getBankStatementVerificationResults(
          files,
          suggestionsResponse
        );
        const bankStatementFormValueWithStorage = {
          ...bankStatementFormValue,
          bankStatementStorage: bankStatementStorage,
        };
        setConfirmFormValues(bankStatementFormValueWithStorage);
        verificationCacheRef.current = {
          files,
          results: bankStatementResults,
          formValues: bankStatementFormValueWithStorage,
        };
        return bankStatementResults;
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        Snackbar.error('Upload failed. Please try again.');
        return selectedUploadContent.requirements.map(() => false);
      }
    },
    [
      getBankStatementSuggestionsAsync,
      getMerchantApplicationUploadUrlAsync,
      Snackbar,
    ]
  );

  const handleBack = useCallback((): void => {
    abortVerification();
    if (stage === 'upload') {
      verificationCacheRef.current = null;
      if (onBackToDirectorDetails) {
        onBackToDirectorDetails();
      } else {
        navigate(PageRoute.Application);
      }
      return;
    }

    if (stage === 'confirm-form') {
      void patchApplicationProgressAsync({
        settlementDetails: ActivationProgressStatus.Upload,
      });
      setStage('upload');
      return;
    }

    navigate(PageRoute.Application);
  }, [abortVerification, navigate, stage, patchApplicationProgressAsync]);

  const handleContinue = useCallback(async (): Promise<void> => {
    if (stage === 'confirm-form') {
      logEvent(PageAction.merchantOnboardingUAConfirmSettlementDetails, {
        merchantApplicationId,
      });

      setSubmitAttempted(true);
      const errors = getConfirmFormErrors(confirmFormValues);

      if (Object.keys(errors).length > 0) {
        return;
      }

      setIsSubmittingConfirmForm(true);

      try {
        const patchPayload =
          buildSettlementDetailsPatchPayload(confirmFormValues);
        await patchMerchantApplicationAsync(patchPayload);
        await patchApplicationProgressAsync({
          settlementDetails: ActivationProgressStatus.Confirmed,
        });
        await submitMerchantApplicationAsync();
        await refreshActivationData();
      } catch (err) {
        if (!isAbortError(err)) {
          Snackbar.error(
            'An error occurred submitting your details. Please try again.'
          );
        }
      } finally {
        setIsSubmittingConfirmForm(false);
      }
      return;
    }

    if (loadedFiles.length === 0) {
      return;
    }

    const runId = verificationRunIdRef.current + 1;
    verificationRunIdRef.current = runId;
    setIsVerifying(true);

    try {
      const results = await uploaderRef.current?.verify();
      if (verificationRunIdRef.current !== runId) {
        return;
      }

      if (results?.every(Boolean)) {
        setStage(
          skipConfirmLoadingRef.current ? 'confirm-form' : 'confirm-loading'
        );
        skipConfirmLoadingRef.current = false;
      }
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('Settlement detail verification flow failed', error);
      }
    } finally {
      if (verificationRunIdRef.current === runId) {
        setIsVerifying(false);
      }
      if (verificationRunIdRef.current !== runId) {
        skipConfirmLoadingRef.current = false;
      }
      verificationAbortControllerRef.current = null;
    }
  }, [confirmFormValues, loadedFiles.length, navigate, stage]);

  useEffect(() => {
    // Skip if already processed
    if (hasAppliedCachePrefillRef.current) {
      return;
    }

    const settlementDetails = activationData?.settlementDetails;

    // Not in edit/confirmed flow - just apply cache prefill if available
    if (!shouldStartAtConfirmFormRef.current) {
      if (settlementDetails?.accountNumber) {
        hasAppliedCachePrefillRef.current = true;
        setConfirmFormValues(
          getBankStatementConfirmFormValuesFromDB(settlementDetails)
        );
      }
      return;
    }

    // In edit/confirmed flow - check if data exists in context
    hasAppliedCachePrefillRef.current = true;

    if (settlementDetails?.accountNumber) {
      // Data exists in context - use it directly (NO loading state)
      setConfirmFormValues(
        getBankStatementConfirmFormValuesFromDB(settlementDetails)
      );
      setStage('confirm-form');
    } else {
      // No data in context - fetch from API (WITH loading state)
      setStage('confirm-loading');
      getActivationData()
        .then((freshData) => {
          const freshSettlementDetails = freshData?.settlementDetails;
          if (freshSettlementDetails?.accountNumber) {
            setConfirmFormValues(
              getBankStatementConfirmFormValuesFromDB(freshSettlementDetails)
            );
          }
        })
        .catch(() => {})
        .finally(() => setStage('confirm-form'));
    }
  }, [activationData, getActivationData]);

  const handleConfirmFieldChange = useCallback(
    (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ): void => {
      const { name, value } = event.target;

      setConfirmFormValues((currentValues) => ({
        ...currentValues,
        [name]: value,
      }));
    },
    []
  );

  const handleEmailFieldChange = useCallback(
    (index: number, value: string): void => {
      setConfirmFormValues((currentValues) => {
        const newEmails = [...currentValues.disbursementEmails];
        newEmails[index] = value;
        return {
          ...currentValues,
          disbursementEmails: newEmails,
        };
      });
    },
    []
  );

  const handleAddEmail = useCallback((): void => {
    setConfirmFormValues((currentValues) => ({
      ...currentValues,
      disbursementEmails: [...currentValues.disbursementEmails, ''],
    }));
  }, []);

  const handleDirectDebitAgreementChange = useCallback(
    (_event: any, checked: boolean): void => {
      setConfirmFormValues((currentValues) => ({
        ...currentValues,
        directDebitAgreementSigned: checked,
        directDebitAgreementSignedTimeStamp: checked
          ? new Date()
          : currentValues.directDebitAgreementSignedTimeStamp,
      }));
    },
    []
  );

  useEffect(() => {
    if (stage === 'upload') {
      logEvent(PageAction.merchantOnboardingPVUploadSettlementDetails, {
        merchantApplicationId,
      });
    } else if (stage === 'confirm-form') {
      logEvent(PageAction.merchantOnboardingPVConfirmSettlementDetails, {
        merchantApplicationId,
      });
    }
  }, [stage]);

  useEffect(() => {
    if (stage !== 'confirm-loading') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setStage('confirm-form');
    }, CONFIRM_DETAILS_LOADING_MS);

    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [stage]);

  const confirmFormErrors = useMemo<ConfirmFormErrors>(
    () => getConfirmFormErrors(confirmFormValues),
    [confirmFormValues]
  );

  const fieldErr = (field: keyof SettlementDetailConfirmFormValues): string =>
    submitAttempted ? (confirmFormErrors[field] ?? '') : '';

  return (
    <ThemeProvider theme={theme}>
      <div
        css={[
          styles.page,
          (stage === 'confirm-loading' || stage === 'confirm-form') &&
            styles.confirmPage,
        ]}
      >
        <div css={styles.panel}>
          {stage === 'upload' && (
            <>
              <div css={styles.section}>
                <div css={styles.section}>
                  <h1 className="heading1" css={styles.title}>
                    {BANKSTATEMENT_UPLOAD_CONTENT.title}
                  </h1>
                  <div css={styles.progressTrack}>
                    <div css={styles.progressFill(progressPercent)} />
                  </div>
                  <p className="body2" css={css.noMargin}>
                    Please provide a copy of the bank statement you wish Zip to
                    use for the settlement of transactions.
                  </p>
                </div>
                <div css={styles.uploaderWrapper}>
                  <GuidedFileUploader
                    ref={uploaderRef}
                    requirements={BANKSTATEMENT_UPLOAD_CONTENT.requirements}
                    onVerify={handleUploaderVerify}
                    onFilesChange={handleFilesChange}
                    multiple={BANKSTATEMENT_UPLOAD_CONTENT.multiple}
                    initialFiles={loadedFiles}
                    initialRequirementResults={
                      verificationCacheRef.current !== null &&
                      loadedFiles.length ===
                        verificationCacheRef.current.files.length &&
                      loadedFiles.every(
                        (f, i) => f === verificationCacheRef.current!.files[i]
                      )
                        ? verificationCacheRef.current.results
                        : null
                    }
                  />
                </div>
              </div>

              <div css={styles.actionRow}>
                <NavButtons
                  canGoBack
                  canContinue={canContinue}
                  isSubmitting={false}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  continueLabel={continueLabel}
                />
              </div>
            </>
          )}

          {stage === 'confirm-loading' && (
            <>
              <div css={styles.confirmHeader}>
                <h1 className="heading1" css={styles.title}>
                  Confirm settlement details
                </h1>
                <div css={styles.progressTrack}>
                  <div css={styles.progressFill(progressPercent)} />
                </div>
              </div>

              <div css={styles.successSection}>
                <div css={styles.successSkeleton(250)} />
                <div css={styles.successSkeleton(182)} />
                <div css={styles.successSkeleton(63)} />
              </div>
            </>
          )}

          {stage === 'confirm-form' && (
            <>
              <div css={styles.confirmHeader}>
                <h1 className="heading1" css={styles.title}>
                  Confirm settlement details
                </h1>
                <div css={styles.progressTrack}>
                  <div css={styles.progressFill(progressPercent)} />
                </div>
              </div>

              <div css={[styles.confirmForm]}>
                <div css={styles.formSection}>
                  <div css={styles.formSectionHeader}>
                    <p className="body1" css={styles.title}>
                      Bank details
                    </p>
                    <p className="body2" css={css.noMargin}>
                      This account will be used to deposit funds from your Zip
                      orders.
                    </p>
                  </div>

                  <div css={styles.formSection}>
                    <TextFields.Outlined
                      type="text"
                      name="accountName"
                      label="Account name *"
                      className="fs-mask"
                      css={styles.textfield}
                      value={confirmFormValues.accountName}
                      onChange={handleConfirmFieldChange}
                      error={Boolean(fieldErr('accountName'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('accountName')),
                        fieldErr('accountName')
                      )}
                      slotProps={{ formHelperText: { className: 'body4' } }}
                    />
                    <TextFields.Outlined
                      type="text"
                      name="bsb"
                      label="BSB *"
                      className="fs-mask"
                      css={styles.textfield}
                      value={confirmFormValues.bsb}
                      onChange={handleConfirmFieldChange}
                      error={Boolean(fieldErr('bsb'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('bsb')),
                        fieldErr('bsb')
                      )}
                      slotProps={{ formHelperText: { className: 'body4' } }}
                    />
                    <TextFields.Outlined
                      type="text"
                      name="accountNumber"
                      label="Account number *"
                      className="fs-mask"
                      css={styles.textfield}
                      value={confirmFormValues.accountNumber}
                      onChange={handleConfirmFieldChange}
                      error={Boolean(fieldErr('accountNumber'))}
                      helperText={renderFieldError(
                        Boolean(fieldErr('accountNumber')),
                        fieldErr('accountNumber')
                      )}
                      slotProps={{ formHelperText: { className: 'body4' } }}
                    />
                  </div>
                </div>

                <div css={styles.formSection}>
                  <div css={styles.formSectionHeader}>
                    <p className="body1" css={styles.title}>
                      Disbursements
                    </p>
                    <p className="body2" css={css.noMargin}>
                      Where would you like us to send your settlement invoices.
                    </p>
                  </div>

                  <div css={styles.formSection}>
                    {confirmFormValues.disbursementEmails.map(
                      (email, index) => {
                        const emailError =
                          submitAttempted &&
                          confirmFormErrors.disbursementEmailErrors?.[index];
                        const generalError =
                          submitAttempted &&
                          index === 0 &&
                          fieldErr('disbursementEmails');

                        return (
                          <div key={index}>
                            <TextFields.Outlined
                              type="email"
                              name={`disbursementEmail-${index}`}
                              label={`Email address${index === 0 ? ' *' : ''}`}
                              className="fs-mask"
                              css={styles.textfield}
                              value={email}
                              onChange={(e) =>
                                handleEmailFieldChange(index, e.target.value)
                              }
                              error={Boolean(emailError || generalError)}
                              helperText={renderFieldError(
                                Boolean(emailError || generalError),
                                emailError || generalError
                              )}
                              slotProps={{
                                formHelperText: { className: 'body4' },
                              }}
                            />
                          </div>
                        );
                      }
                    )}
                    <Buttons.Text
                      onClick={handleAddEmail}
                      disabled={
                        !confirmFormValues.disbursementEmails[
                          confirmFormValues.disbursementEmails.length - 1
                        ] ||
                        confirmFormValues.disbursementEmails.length >=
                          MAX_EMAIL_ADDRESSES
                      }
                      css={styles.addEmailBtn}
                    >
                      Add another email
                    </Buttons.Text>
                  </div>
                </div>
                <div css={styles.formSection}>
                  <div>
                    <Widgets.Checkbox
                      label={
                        <p>
                          I authorise the direct debit and agree to the terms of
                          the{' '}
                          <a
                            role="button"
                            tabIndex={0}
                            onClick={(e): void => {
                              e.preventDefault();
                              setIsDirectDebitDialogOpen(true);
                            }}
                          >
                            Direct Debit Request and the Direct Debit Service
                            Agreement.{' '}
                          </a>
                          Direct debit only occurs when a refund is placed. This
                          is not related to any service or transactional fees
                          when using Zip.
                        </p>
                      }
                      checked={confirmFormValues.directDebitAgreementSigned}
                      onChange={handleDirectDebitAgreementChange}
                    />
                    <p css={[styles.textfield, styles.errorTextField]}>
                      {renderFieldError(
                        Boolean(fieldErr('directDebitAgreementSigned')),
                        fieldErr('directDebitAgreementSigned')
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div css={styles.actionRow}>
                <NavButtons
                  canGoBack
                  canContinue={canContinue}
                  isSubmitting={isSubmittingConfirmForm}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  continueLabel={continueLabel}
                />
              </div>
              <DirectDebitDialog
                open={isDirectDebitDialogOpen}
                toggleOpen={setIsDirectDebitDialogOpen}
              />
            </>
          )}

          {isVerifying && (
            <div css={styles.overlay}>
              <div css={styles.verificationCard}>
                <div css={styles.verificationIllustration} aria-hidden="true">
                  <img
                    src={VerifySettlementIllustration}
                    alt="settlement detail illustration"
                    css={styles.verificationIllustrationImage}
                  />
                </div>

                <div css={styles.verificationContent}>
                  <p className="heading2" css={styles.verificationTitle}>
                    Verifying your bank details
                  </p>

                  <div css={styles.verificationTrack}>
                    <div css={styles.verificationFill} />
                  </div>

                  <Buttons.Text
                    onClick={handleCancelVerification}
                    css={styles.cancelButton}
                  >
                    Cancel
                  </Buttons.Text>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default SettlementDetailsStepModule;
